/**
 * LiveRegion Component
 * Provides ARIA live region for screen reader announcements
 * Complements visual toast notifications with accessibility announcements
 */

import { useEffect, useRef } from 'react';

export interface LiveRegionProps {
  /**
   * Message to announce to screen readers
   */
  message: string;

  /**
   * Politeness level for the announcement
   * - 'polite': Wait for current speech to finish (default)
   * - 'assertive': Interrupt current speech (use for important messages)
   * @default 'polite'
   */
  politeness?: 'polite' | 'assertive';

  /**
   * Whether to clear the message after announcement
   * @default true
   */
  clearAfterAnnounce?: boolean;
}

/**
 * LiveRegion for screen reader announcements
 *
 * This component creates an ARIA live region that announces messages to screen readers
 * without disrupting the visual UI. It complements toast notifications by ensuring
 * all important state changes are accessible to users with screen readers.
 *
 * @example
 * ```tsx
 * const [announcement, setAnnouncement] = useState('');
 *
 * // Announce when data loads
 * useEffect(() => {
 *   if (alerts.length > 0) {
 *     setAnnouncement(`${alerts.length} alerts loaded`);
 *   }
 * }, [alerts]);
 *
 * return <LiveRegion message={announcement} />;
 * ```
 *
 * @example
 * ```tsx
 * // Assertive announcement for errors
 * <LiveRegion
 *   message="Error: Failed to save settings"
 *   politeness="assertive"
 * />
 * ```
 */
export function LiveRegion({
  message,
  politeness = 'polite',
  clearAfterAnnounce = true,
}: LiveRegionProps) {
  const announceTimeoutRef = useRef<number | null>(null);
  const messageRef = useRef<string>('');

  useEffect(() => {
    // Only announce if message changed
    if (message && message !== messageRef.current) {
      messageRef.current = message;

      // Clear previous timeout if exists
      if (announceTimeoutRef.current) {
        clearTimeout(announceTimeoutRef.current);
      }

      // Clear message after announcement to allow re-announcing same message
      if (clearAfterAnnounce) {
        announceTimeoutRef.current = window.setTimeout(() => {
          messageRef.current = '';
        }, 1000);
      }
    }

    return () => {
      if (announceTimeoutRef.current) {
        clearTimeout(announceTimeoutRef.current);
      }
    };
  }, [message, clearAfterAnnounce]);

  if (!message) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live={politeness}
      aria-atomic="true"
      style={{
        position: 'absolute',
        left: '-10000px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    >
      {message}
    </div>
  );
}
